import set from 'lodash/set';

import { NUI_SCHEMA_NAMES } from 'lib/contract';
import { isBlank } from 'lib/string';

/**
 * check whether a given object is an experiment component
 * this is based on Variation with a fallback to SchemaName
 * @param {object} comp - component object
 * @param {string} comp.SchemaName - the given component's SchemaName
 * @param {string} comp.Variation - the given component's Variation
 * @returns {boolean} whether the given object is an experiment object or not
 */
export const isExperiment = function isExperimentComponent({
  SchemaName = '',
  Variation = SchemaName
}) {
  return Variation === NUI_SCHEMA_NAMES.EXPERIMENT;
};

/**
 * filter components based on hidden experiments
 *
 * @param {object} p - function params
 * @param {object[]} [p.components=[]] - array of flattened components
 * @param {object} [p.hiddenExperiments={}] - object mapping experiment names to list of variants to hide
 * @returns {{hide: string[], keep: object[]}} object with strings to hide and objects to keep
 */
export const experimentsFilter = function experimentsFilter({
  components = [],
  hiddenExperiments = {}
}) {
  const hide = [];
  const keep = components.reduce((filtered, component) => {
    // keep non-experiment components
    if (!isExperiment(component)) {
      return filtered.concat(component);
    }

    const { experimentName, variants } = component;
    const keep = variants
      .filter(({ data, variant }) => {
        const shouldKeep = !hiddenExperiments?.[experimentName]?.includes(variant);
        if (!shouldKeep) {
          hide.push(data.Id);
        }
        return shouldKeep;
      })
      .map(({ data, variant }) => ({
        ...data,
        contentExperiment: { experimentName, variant }
      }));

    // hide experiment container if there's nothing to keep
    if (!keep.length) {
      hide.push(component.Id);
    }

    return filtered.concat(keep);
  }, []);
  return { hide, keep };
};

/**
 * get all experiment names and variants in an array of components
 *
 * @param {object[]} [components=[]] - array of component objects
 * @returns {object} object with experiments names as keys and variants as values
 */
export const getExperimentsAndVariants = function getExperimentsAndVariants(components = []) {
  return components.filter(isExperiment).reduce((experiments, component) => {
    const { experimentName = '', variants = [] } = component.Payload ?? {};
    if (isBlank(experimentName)) {
      return experiments;
    }

    const allVariants = variants
      .map(e => e.variant)
      .concat(experiments?.[experimentName] ?? []);
    return set(experiments, experimentName, [...new Set(allVariants)].sort());
  }, {});
};
